import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  api,
  unwrap,
  type FilenameProfileInput,
  type ProfileSuggestion,
} from "../api/client";

/** Names handed over from another page (e.g. an import about to start) as the first examples. */
let handedOver: string[] = [];
export function suggestFrom(names: string[]) {
  handedOver = names;
}

/** Suggests profiles from example file names; only the names are read, never the files. */
export function ProfileGenerator({
  onUse,
}: {
  onUse: (draft: FilenameProfileInput, examples: string[]) => void;
}) {
  const [names, setNames] = useState(() => {
    const given = handedOver.join("\n");
    handedOver = [];
    return given;
  });
  const files = useRef<HTMLInputElement>(null);
  const folder = useRef<HTMLInputElement | null>(null);
  const list = names
    .split("\n")
    .map((n) => n.trim())
    .filter(Boolean);

  const append = (more: string[]) =>
    setNames((current) =>
      [
        ...new Set([
          ...current
            .split("\n")
            .map((n) => n.trim())
            .filter(Boolean),
          ...more,
        ]),
      ].join("\n"),
    );

  const unmatched = useMutation({
    mutationFn: () => unwrap(api.GET("/api/profiles/unmatched")),
    onSuccess: append,
  });
  const generate = useMutation({
    mutationFn: () =>
      unwrap(api.POST("/api/profiles/generate", { body: { filenames: list } })),
  });

  const pick = (picked: FileList | null) =>
    append(Array.from(picked ?? []).map((f) => f.webkitRelativePath || f.name));

  const use = (s: ProfileSuggestion) =>
    onUse(
      {
        device: "",
        type: s.type,
        pattern: s.pattern,
        ts_format: s.ts_format,
        utc_offset_minutes: 0,
        source_template: s.source_template,
        pps_aligned: false,
        ts_is_session_start: false,
        priority: 100,
        enabled: true,
      },
      s.examples,
    );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          Suggest profiles from example names
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add file names from one device, then get a suggested profile for each
          way it names its files. Only the names are used; no files are
          uploaded.
        </Typography>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Button onClick={() => files.current?.click()}>Choose files</Button>
          <Button onClick={() => folder.current?.click()}>
            Choose a folder
          </Button>
          <Button
            onClick={() => unmatched.mutate()}
            disabled={unmatched.isPending}
          >
            Add unrecognised imported files
          </Button>
          <input
            ref={files}
            type="file"
            multiple
            hidden
            aria-label="Example files"
            onChange={(e) => {
              pick(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={(el) => {
              folder.current = el;
              el?.setAttribute("webkitdirectory", "");
            }}
            type="file"
            hidden
            aria-label="Example folder"
            onChange={(e) => {
              pick(e.target.files);
              e.target.value = "";
            }}
          />
        </Stack>
        {unmatched.isSuccess && unmatched.data.length === 0 && (
          <Alert severity="info">
            No imported files are waiting for a profile.
          </Alert>
        )}
        {unmatched.isError && (
          <Alert severity="error">{unmatched.error.message}</Alert>
        )}
        <TextField
          label="Example file names, one per line"
          multiline
          minRows={4}
          maxRows={12}
          value={names}
          onChange={(e) => setNames(e.target.value)}
          slotProps={{
            htmlInput: {
              style: { fontFamily: "monospace" },
              spellCheck: false,
            },
          }}
        />
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button
            variant="contained"
            disabled={!list.length || generate.isPending}
            onClick={() => generate.mutate()}
          >
            Suggest profiles
          </Button>
          <Button disabled={!names} onClick={() => setNames("")}>
            Clear
          </Button>
        </Stack>
        {generate.isError && (
          <Alert severity="error">{generate.error.message}</Alert>
        )}
        {generate.isSuccess && generate.data.suggestions.length === 0 && (
          <Alert severity="warning">
            No date and time found in these names, so no profile can be
            suggested.
          </Alert>
        )}
        {generate.data?.suggestions.map((s) => (
          <Paper key={s.pattern} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 2, flexWrap: "wrap" }}
              >
                <Typography sx={{ fontWeight: 500, flexGrow: 1 }}>
                  {s.type}
                </Typography>
                <Button variant="outlined" onClick={() => use(s)}>
                  Use this
                </Button>
              </Stack>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {s.pattern}
              </Typography>
              <Typography variant="body2">
                Time format <code>{s.ts_format}</code> · Source{" "}
                <code>{s.source_template}</code> · Matches {s.examples.length}{" "}
                example{s.examples.length === 1 ? "" : "s"}
              </Typography>
              {s.notes.map((n) => (
                <Typography key={n} variant="body2" color="text.secondary">
                  {n}
                </Typography>
              ))}
            </Stack>
          </Paper>
        ))}
        {(generate.data?.unusable.length ?? 0) > 0 && (
          <Alert severity="info">
            No date and time found in:{" "}
            {generate.data?.unusable.slice(0, 10).join(", ")}
            {(generate.data?.unusable.length ?? 0) > 10 ? "…" : ""}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
